import React, { useState, useEffect } from 'react';
import { PieChart, Building2, Users, Coins, HardDrive, Scale, FileText, Bot, MessageSquare, Save, Loader2, Utensils } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Visual CAPEX Split Card - Shows who invests what (CCD vs CLIENT)
 * Note: For Système U, CCD always carries all CAPEX regardless of investment breakdown
 */
export const CapexSplitCard = ({ 
  capexInvestment, 
  totalCapex,
  ccdCapex,
  clientCapex,
  language = 'fr',
  enseigne = '',
  simulationId = null,
}) => {
  const { user, token } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [comments, setComments] = useState(capexInvestment?.capex_comments || {});
  const [editingKey, setEditingKey] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [draftValue, setDraftValue] = useState('');

  // Re-sync when capexInvestment changes (e.g. after save)
  useEffect(() => {
    setComments(capexInvestment?.capex_comments || {});
  }, [capexInvestment]);

  if (!capexInvestment) return null;

  const startEdit = (key) => {
    setEditingKey(key);
    setDraftValue(comments[key] || '');
  };

  const saveComment = async (key) => {
    if (!simulationId || !token) {
      setEditingKey(null);
      return;
    }
    setSavingKey(key);
    try {
      const next = { ...comments, [key]: draftValue };
      const res = await fetch(`${API}/api/simulations/${simulationId}/capex-comments`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ capex_comments: next }),
      });
      if (res.ok) {
        setComments(next);
        toast.success(language === 'fr' ? 'Commentaire enregistré' : 'Comment saved');
        setEditingKey(null);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur réseau'); }
    finally { setSavingKey(null); }
  };

  // Calculate split from investment data if not provided
  const calculateSplit = () => {
    // Calculate Corner + Vitrine + Inox totals by investor
    let clientMain = 0;
    let ccdMain = 0;

    // Corner
    if (capexInvestment.corner_investor === 'CCD') {
      ccdMain += capexInvestment.capex_corner || 0;
    } else {
      clientMain += capexInvestment.capex_corner || 0;
    }

    // Vitrine
    if (capexInvestment.vitrine_investor === 'CCD') {
      ccdMain += capexInvestment.capex_vitrine || 0;
    } else {
      clientMain += capexInvestment.capex_vitrine || 0;
    }

    // Inox
    if (capexInvestment.inox_investor === 'CCD') {
      ccdMain += capexInvestment.capex_inox || 0;
    } else {
      clientMain += capexInvestment.capex_inox || 0;
    }

    // Inox Labo (si "Labo à équiper ?" = Oui)
    const inoxLabo = capexInvestment.labo_to_equip ? (capexInvestment.capex_inox_labo || 0) : 0;
    if (inoxLabo) {
      if ((capexInvestment.labo_inox_investor || 'CCD') === 'CCD') ccdMain += inoxLabo;
      else clientMain += inoxLabo;
    }

    // Machine à tapisser le riz (toujours CCD)
    const riceMachine = capexInvestment.rice_machine ? (capexInvestment.capex_rice_machine || 0) : 0;

    // Determine majority investor (CLIENT vs CCD for Corner+Vitrine+Inox)
    // Reflects the actual investor choice (incl. Système U). Commission grid unaffected.
    const clientIsMajority = clientMain >= ccdMain;

    // Total CAPEX (everything)
    const total = (capexInvestment.capex_corner || 0) + 
                  (capexInvestment.capex_vitrine || 0) + 
                  (capexInvestment.capex_inox || 0) + 
                  (capexInvestment.capex_computer_cash || 0) + 
                  (capexInvestment.capex_local_legal || 0) + 
                  (capexInvestment.capex_robots || 0) +
                  inoxLabo + riceMachine;

    // Per-item (poste par poste) allocation: each poste is paid by its own investor.
    //  - Informatique/Caisse: ALWAYS CCD
    //  - Machine à riz: ALWAYS CCD
    //  - Frais légaux + Robots: follow the majority carrier
    const computer = capexInvestment.capex_computer_cash || 0;
    const followMajority = (capexInvestment.capex_local_legal || 0) + (capexInvestment.capex_robots || 0);
    const ccdPayment = ccdMain + computer + riceMachine + (clientIsMajority ? 0 : followMajority);
    const clientOwnership = clientMain + (clientIsMajority ? followMajority : 0);

    return { 
      ccd: ccdPayment, 
      client: clientOwnership, 
      total, 
      clientIsMajority 
    };
  };

  const split = calculateSplit();
  const actualCcd = ccdCapex ?? split.ccd;
  const actualClient = clientCapex ?? split.client;
  const actualTotal = totalCapex ?? split.total;

  const ccdPercent = actualTotal > 0 ? (actualCcd / actualTotal) * 100 : 0;
  const clientPercent = actualTotal > 0 ? (actualClient / actualTotal) * 100 : 0;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  // Determine who is the main investor
  const mainInvestor = actualCcd > actualClient ? 'CCD' : actualClient > actualCcd ? 'CLIENT' : 'ÉGAL';

  // Check if CLIENT is majority (from calculation or passed value)
  const clientIsMajority = split.clientIsMajority !== undefined ? split.clientIsMajority : actualClient >= actualCcd;

  // Build items list with investor info
  const capexItems = [
    {
      key: 'corner',
      label: language === 'fr' ? 'Corner/Mobilier' : 'Corner/Furniture',
      amount: capexInvestment.capex_corner || 0,
      investor: capexInvestment.corner_investor || 'CCD',
      icon: Building2
    },
    {
      key: 'vitrine',
      label: language === 'fr' ? 'Vitrine' : 'Display Case',
      amount: capexInvestment.capex_vitrine || 0,
      investor: capexInvestment.vitrine_investor || 'CCD',
      icon: Scale
    },
    {
      key: 'inox',
      label: 'Inox',
      amount: capexInvestment.capex_inox || 0,
      investor: capexInvestment.inox_investor || 'CCD',
      icon: HardDrive
    },
    {
      key: 'inox_labo',
      label: language === 'fr' ? 'Inox Labo' : 'Lab Inox',
      amount: capexInvestment.labo_to_equip ? (capexInvestment.capex_inox_labo || 0) : 0,
      investor: capexInvestment.labo_inox_investor || 'CCD',
      icon: HardDrive
    },
    {
      key: 'rice_machine',
      label: language === 'fr' ? 'Machine à tapisser riz' : 'Rice machine',
      amount: capexInvestment.rice_machine ? (capexInvestment.capex_rice_machine || 0) : 0,
      investor: 'CCD',
      icon: Utensils
    },
    {
      key: 'computer',
      label: language === 'fr' ? 'Informatique/Caisse' : 'Computer/Cash Register',
      amount: capexInvestment.capex_computer_cash || 0,
      investor: 'CCD', // Always CCD
      icon: Coins
    },
    {
      key: 'legal',
      label: language === 'fr' ? 'Frais Locaux/Juridiques' : 'Local/Legal Fees',
      amount: capexInvestment.capex_local_legal || 0,
      investor: clientIsMajority ? 'CLIENT' : 'CCD', // Follows majority
      icon: FileText
    },
    {
      key: 'robots',
      label: 'Robots',
      amount: capexInvestment.capex_robots || 0,
      investor: clientIsMajority ? 'CLIENT' : 'CCD', // Follows majority
      icon: Bot
    }
  ].filter(item => item.amount > 0);

  return (
    <div 
      className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-xl p-5 border border-slate-200 dark:border-slate-700"
      data-testid="capex-split-card"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
          <PieChart className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900 dark:text-white">
            {language === 'fr' ? 'Répartition CAPEX' : 'CAPEX Split'}
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {language === 'fr' ? 'Qui investit quoi' : 'Who invests what'}
          </p>
        </div>
        {/* Majority Badge */}
        <div className={`px-3 py-1 rounded-full text-xs font-bold ${
          clientIsMajority 
            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200' 
            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
        }`}>
          {language === 'fr' ? 'Porteur majoritaire: ' : 'Majority carrier: '}
          {clientIsMajority ? 'CLIENT' : 'CCD'}
        </div>
      </div>

      {/* CAPEX decomposition line (poste par poste) */}
      <div className="mb-4 rounded-lg bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 p-3" data-testid="capex-decomposition-line">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
            {language === 'fr' ? 'Décomposition du CAPEX' : 'CAPEX decomposition'}
          </span>
          <span className="text-sm font-bold font-mono text-slate-900 dark:text-white">
            {language === 'fr' ? 'Total : ' : 'Total: '}{formatCurrency(actualTotal)}
          </span>
        </div>
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
          <div className="bg-blue-500 transition-all" style={{ width: `${ccdPercent}%` }} title="CCD"></div>
          <div className="bg-amber-500 transition-all" style={{ width: `${clientPercent}%` }} title="CLIENT"></div>
        </div>
        <div className="flex items-center justify-between mt-2 text-xs">
          <span className="text-blue-600 dark:text-blue-400 font-medium" data-testid="capex-ccd-amount">
            CCD : {formatCurrency(actualCcd)} ({Math.round(ccdPercent)}%)
          </span>
          <span className="text-amber-600 dark:text-amber-400 font-medium" data-testid="capex-client-amount">
            CLIENT : {formatCurrency(actualClient)} ({Math.round(clientPercent)}%)
          </span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {/* CCD carries (cash flow) */}
        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 border border-blue-200 dark:border-blue-800" data-testid="capex-ccd-card">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
              {language === 'fr' ? 'CCD porte (Cash-flow)' : 'CCD carries (Cash flow)'}
            </span>
          </div>
          <p className="text-xl font-bold text-blue-900 dark:text-blue-100 font-mono">
            {formatCurrency(actualCcd)}
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            {actualCcd >= actualTotal
              ? (language === 'fr' ? 'CCD porte tout le CAPEX' : 'CCD carries all CAPEX')
              : (language === 'fr' ? 'Postes CCD + Informatique' : 'CCD items + IT')}
          </p>
        </div>

        {/* CLIENT carries */}
        <div className="bg-amber-50 dark:bg-amber-900/30 rounded-lg p-3 border border-amber-200 dark:border-amber-800" data-testid="capex-client-card">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
              {language === 'fr' ? 'CLIENT porte' : 'CLIENT carries'}
            </span>
          </div>
          <p className="text-xl font-bold text-amber-900 dark:text-amber-100 font-mono">
            {formatCurrency(actualClient)}
          </p>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            {actualClient <= 0
              ? (language === 'fr' ? 'Aucun poste porté' : 'No items carried')
              : (language === 'fr' ? 'Postes financés par le client' : 'Items funded by client')}
          </p>
        </div>
      </div>

      {/* Detailed Breakdown */}
      <details className="group">
        <summary className="text-sm text-slate-600 dark:text-slate-400 cursor-pointer hover:text-slate-900 dark:hover:text-white flex items-center gap-2">
          <span className="group-open:rotate-90 transition-transform">▶</span>
          {language === 'fr' ? 'Voir le détail par poste' : 'View breakdown by item'}
        </summary>
        <div className="mt-3 space-y-2">
          {capexItems.map((item) => {
            const Icon = item.icon;
            const isCcd = item.investor === 'CCD';
            const itemComment = comments[item.key] || '';
            const isEditing = editingKey === item.key;
            return (
              <div 
                key={item.key}
                className={`p-2 rounded-lg space-y-1.5 ${
                  isCcd 
                    ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800' 
                    : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className={`w-4 h-4 flex-shrink-0 ${isCcd ? 'text-blue-500' : 'text-amber-500'}`} />
                    <span className="text-sm text-slate-700 dark:text-slate-300 truncate">{item.label}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      isCcd 
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-200' 
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-800 dark:text-amber-200'
                    }`}>
                      {item.investor}
                    </span>
                    <span className="text-sm font-mono font-medium text-slate-900 dark:text-white">
                      {formatCurrency(item.amount)}
                    </span>
                    {isAdmin && simulationId && !isEditing && (
                      <button
                        type="button"
                        onClick={() => startEdit(item.key)}
                        className={`p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors ${itemComment ? 'text-emerald-600' : 'text-slate-400'}`}
                        title={itemComment ? (language === 'fr' ? 'Modifier le commentaire' : 'Edit comment') : (language === 'fr' ? 'Ajouter un commentaire' : 'Add comment')}
                        data-testid={`capex-comment-edit-${item.key}`}
                      >
                        <MessageSquare className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                {/* Comment row */}
                {isEditing ? (
                  <div className="flex items-center gap-2 pl-6">
                    <input
                      type="text"
                      value={draftValue}
                      onChange={(e) => setDraftValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveComment(item.key);
                        if (e.key === 'Escape') setEditingKey(null);
                      }}
                      placeholder={language === 'fr' ? 'Commentaire interne (Entrée pour sauver)' : 'Internal comment (Enter to save)'}
                      className="flex-1 text-xs px-2 py-1 rounded bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      autoFocus
                      maxLength={1000}
                      data-testid={`capex-comment-input-${item.key}`}
                    />
                    <button
                      type="button"
                      onClick={() => saveComment(item.key)}
                      disabled={savingKey === item.key}
                      className="p-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                      title="Sauvegarder"
                    >
                      {savingKey === item.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingKey(null)}
                      className="text-xs text-slate-500 hover:text-slate-700 px-1"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  itemComment && (
                    <div className="pl-6 text-xs italic text-slate-600 dark:text-slate-400 flex items-start gap-1.5">
                      <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0 text-emerald-500" />
                      <span className="break-words">{itemComment}</span>
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      </details>

      {/* Impact Notice */}
      {clientIsMajority && (
        <div className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
          <p className="text-sm text-emerald-700 dark:text-emerald-300">
            <span className="font-semibold">💡 {language === 'fr' ? 'Impact' : 'Impact'}:</span>{' '}
            {language === 'fr' 
              ? `CLIENT porte le CAPEX total (${formatCurrency(actualTotal)}). Seul le paiement CCD (${formatCurrency(actualCcd)} - Frais Opérationnels) est déduit du cash-flow.`
              : `CLIENT carries total CAPEX (${formatCurrency(actualTotal)}). Only CCD payment (${formatCurrency(actualCcd)} - Operational costs) is deducted from cash flow.`
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default CapexSplitCard;
